import React from "react";

export interface SparklineProps {
  /** Data points, oldest → newest. */
  values: number[];
  width?: number;
  height?: number;
  /** Optional per-point labels used for the native SVG tooltip. */
  labels?: string[];
  /** Accessible / hover description prefix, e.g. the metric label. */
  title?: string;
}

const PAD = 3;

/**
 * Dependency-free inline sparkline. Strokes with `currentColor` so the caller
 * controls the hue (and it stays legible on any background); the last point is
 * emphasised with a filled dot. All-zero (or single-value) series render as a
 * flat baseline through the vertical middle rather than a degenerate line.
 */
const Sparkline: React.FC<SparklineProps> = ({
  values,
  width = 120,
  height = 28,
  labels,
  title,
}) => {
  if (!values || values.length === 0) return null;

  const innerW = Math.max(1, width - PAD * 2);
  const innerH = Math.max(1, height - PAD * 2);

  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min;
  const flat = span === 0;

  const x = (i: number) =>
    values.length === 1 ? PAD + innerW / 2 : PAD + (i * innerW) / (values.length - 1);
  // Higher value → smaller y. A flat series sits on the vertical middle.
  const y = (v: number) =>
    flat ? PAD + innerH / 2 : PAD + innerH - ((v - min) / span) * innerH;

  const points = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`);
  const lastX = x(values.length - 1);
  const lastY = y(values[values.length - 1]);

  const hover = [
    title,
    labels && labels.length === values.length
      ? labels
          .map((l, i) => `${l}: ${values[i]}`)
          .slice(-12)
          .join(" · ")
      : values.join(" · "),
  ]
    .filter(Boolean)
    .join(" — ");

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={hover}
      style={{ display: "block", overflow: "visible" }}
    >
      <title>{hover}</title>
      <polyline
        points={points.join(" ")}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={flat ? 0.45 : 1}
      />
      <circle cx={lastX} cy={lastY} r={2.4} fill="currentColor" />
    </svg>
  );
};

export default Sparkline;
